import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';

import { useDeleteTenant, useTenants } from '@/data/tenant';

const formSchema = z.object({
  is_confirmed: z.boolean().refine((val) => val === true, {
    message: 'You must confirm this action by checking the box.'
  })
});

const DeleteTenantDialog = ({ tenant, setOpen }) => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const deleteTenant = useDeleteTenant();
  const tenants = useTenants();
  const navigate = useNavigate();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      is_confirmed: false
    }
  });

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      setApiError('');
      await deleteTenant(tenant.id, values);
      await tenants.mutate(
        (currentTenants) => currentTenants.filter((t) => t.id !== tenant.id),
        false
      );
      setSubmitting(false);
      setOpen(false);
      navigate('/tenant');
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={setOpen}>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Delete Tenant {tenant.name}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="is_confirmed"
              render={({ field }) => (
                <FormItem className="">
                  <div className="flex items-center gap-2 space-y-0 ">
                    <FormControl className="flex items-center ">
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>I want to delete this tenant</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting} variant="destructive">
                Delete Tenant
              </SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteTenantDialog;
